import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
    type CustomList,
    type CustomListDetail,
    type CustomListItem,
    fetchCustomListDetail,
} from 'entities/CustomList';
import { getRouteOrdersAddItemsList } from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';

export const useAddListToOrderConfirm = (
    listId: string | undefined,
    detail: CustomListDetail | null | undefined,
) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [listItemsCache, setListItemsCache] = useState<
        Record<string, CustomListItem[]>
    >({});
    const [confirmList, setConfirmList] = useState<{
        list: CustomList;
        items: CustomListItem[];
    } | null>(null);

    useEffect(() => {
        if (detail?.id && detail.id === listId) {
            setListItemsCache((prev) => ({
                ...prev,
                [detail.id]: detail.items || [],
            }));
        }
    }, [detail, listId]);

    const openConfirmForList = useCallback(
        async (list: CustomList, items?: CustomListItem[]) => {
            if (list.itemCount === 0) {
                return;
            }

            let nextItems = items || listItemsCache[list.id];
            if (!nextItems) {
                const result = await dispatch(
                    fetchCustomListDetail({
                        listId: list.id,
                        updateDetail: list.id === listId,
                    }),
                );
                if (fetchCustomListDetail.fulfilled.match(result)) {
                    const fetchedItems = result.payload.items || [];
                    nextItems = fetchedItems;
                    setListItemsCache((prev) => ({
                        ...prev,
                        [list.id]: fetchedItems,
                    }));
                } else {
                    const payloadMessage =
                        typeof result.payload === 'string'
                            ? result.payload
                            : '';

                    toast.error(
                        payloadMessage ||
                            t('customLists.failedToLoadListItems'),
                    );
                    return;
                }
            }

            if (!nextItems?.length) {
                return;
            }

            setConfirmList({ list, items: nextItems });
        },
        [dispatch, listId, listItemsCache, t],
    );

    const closeConfirm = useCallback(() => {
        setConfirmList(null);
    }, []);

    const onConfirmSuccess = useCallback(() => {
        if (!listId && confirmList) {
            navigate(getRouteOrdersAddItemsList(confirmList.list.id), {
                state: location.state,
            });
        }
    }, [confirmList, listId, location.state, navigate]);

    return {
        openConfirmForList,
        confirmList,
        closeConfirm,
        onConfirmSuccess,
    };
};
